import React, { isValidElement } from 'react';
import PropTypes from 'prop-types';
import {
  Completed as CompletedIcon,
  Alert as AlertIcon
} from '@dls/assets/icons';
import Card from '../Card';
import {
  Box,
  Column,
  Columns,
  LeftContainer,
  RightContainer,
  ListItemImage,
  ListItemThumbnail,
  SubTitle,
  TagWrapper,
  Tag,
  Title,
  ListItemPointsContainer
} from './style';
import {
  createLegoComponent,
  getLegoElementName
} from '../../core/utils/legoUtils';
import { getFilteredList } from '../../core/utils/getFilteredList';
import { testIdProps } from '../../helpers';
import { resizeIcon } from '../../helpers/utils';
import Toggle from '../Toggle';
import { useTheme, ThemeProvider } from '../../core/ThemeProvider';

const STATUS_TYPES = {
  success: { Icon: CompletedIcon, color: 'cl_successGreen' },
  error: { Icon: AlertIcon, color: 'cl_errorRed' }
};

const ListItem = p => {
  const {
    dataTestId,
    onClick,
    leftContent,
    body,
    rightContent,
    bordered,
    maxTitleLine,
    maxSubtitleLine
  } = p;

  const isListItemTall =
    getLegoElementName(leftContent) === 'ListItemThumbnail';
  const tagContent = isListItemTall ? getFilteredList(body, 'tag')[0] : null;
  const isBordered = isListItemTall || bordered;

  const renderTag = () => {
    if (tagContent) {
      return isValidElement(tagContent.tag) ? (
        <TagWrapper>{tagContent.tag}</TagWrapper>
      ) : (
        <Tag key={tagContent.tag}>{tagContent.tag}</Tag>
      );
    }
    return null;
  };

  const renderChild = () => {
    return (
      <Columns tall={isListItemTall} onClick={onClick}>
        {/* Render left content */}
        {leftContent && (
          <Column>
            <LeftContainer bordered={isBordered} tall={isListItemTall}>
              {leftContent}
              {renderTag()}
            </LeftContainer>
          </Column>
        )}

        {/* Render body content */}
        <Column width="content">
          <Box
            bordered={isBordered}
            isFirst={!leftContent}
            isLast={!rightContent}
          >
            {body &&
              renderBody(body, isListItemTall, {
                maxTitleLine,
                maxSubtitleLine
              })}
          </Box>
        </Column>

        {/* TODO: Render right content */}
        <Column>
          {renderRightContent(rightContent, { bordered: isBordered })}
        </Column>
      </Columns>
    );
  };

  return (
    <ThemeProvider theme={{ button: { minWidth: 'fit-content' } }}>
      <div {...testIdProps(p, dataTestId)}>
        {isBordered ? <Card>{renderChild()}</Card> : renderChild()}
      </div>
    </ThemeProvider>
  );
};

ListItem.defaultProps = {
  dataTestId: 'list-item',
  leftContent: null,
  rightContent: null,
  bordered: true,
  maxTitleLine: 1,
  maxSubtitleLine: 1
};

ListItem.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // |     To update them edit the d.ts file and run "yarn proptypes"     |
  // ----------------------------------------------------------------------
  /**
   * Data model for body content of the list item
   * Currently it support 3 types of body content
   * 1. title
   * 2. sub title
   * 3. tag
   *
   */
  body: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({ title: PropTypes.string }),
      PropTypes.shape({ subTitle: PropTypes.string }),
      PropTypes.shape({ tag: PropTypes.string }),
      PropTypes.shape({ points: PropTypes.element })
    ])
  ),
  /**
   * Toggles rendering of the border around the list item
   */
  bordered: PropTypes.bool,
  leftContent: PropTypes.shape({
    type: PropTypes.oneOf([ListItemImage, ListItemThumbnail])
  }),
  /**
   * The max line of subtitle
   */
  maxSubtitleLine: PropTypes.number,
  /**
   * The max line of title
   */
  maxTitleLine: PropTypes.number,
  rightContent: PropTypes.shape({
    toggle: PropTypes.shape({
      value: PropTypes.string,
      onChange: PropTypes.func
    }),
    status: PropTypes.oneOf(['success', 'error'])
  })
};

const ListItemIcon = createLegoComponent('ListItemIcon', ({ icon: Icon }) => {
  return resizeIcon(Icon);
});

const ListItemStatus = createLegoComponent('ListItemStatus', p => {
  const status = STATUS_TYPES[p.type];
  if (!status) return null;
  const { theme } = useTheme();
  const { Icon, color } = status;
  return <Icon color={theme[color]} size={24} />;
});

const ListItemToggle = createLegoComponent('ListItemToggle', Toggle);

const ListItemPoints = createLegoComponent('ListItemPoints', p => {
  const { text, icon: Icon } = p;
  const { theme } = useTheme();
  return (
    <ListItemPointsContainer>
      <Icon size={theme.listItem.sizeIcon} color={theme.listItem.colorIcon} />
      <span>{text}</span>
    </ListItemPointsContainer>
  );
});

ListItem.Icon = ListItemIcon;
ListItem.Image = ListItemImage;
ListItem.Thumbnail = ListItemThumbnail;
ListItem.Toggle = ListItemToggle;
ListItem.Status = ListItemStatus;
ListItem.Points = ListItemPoints;

export default ListItem;

/**
 * Render main body content
 * @param bodyProps
 * @param bodyProps.title
 * @param bodyProps.subTitle
 * @param bodyProps.tag
 * @param bodyProps.points
 * @return {any[]}
 */
function renderBody(bodyProps = [], isListItemTall = false, option) {
  const newBodyProps = isListItemTall
    ? bodyProps
    : getFilteredList(bodyProps, ['points', 'tag'], true);

  return newBodyProps.map(({ title, subTitle, points }, index) => {
    if (title)
      return (
        <Title key={title + index} maxLine={option.maxTitleLine}>
          {title}
        </Title>
      );

    if (subTitle)
      return (
        <SubTitle key={subTitle + index} maxLine={option.maxSubtitleLine}>
          {subTitle}
        </SubTitle>
      );

    if (points) return <div key={index}>{points}</div>;
  });
}

function renderRightContent(content, option) {
  const legoElName = getLegoElementName(content);
  const hasOnClick = !!content?.props?.onClick;

  if (legoElName) {
    return <RightContainer bordered>{content}</RightContainer>;
  }
  /**
   * Content is not a ListItem lego block.
   * render as is.
   * */
  return (
    <RightContainer
      onClick={hasOnClick ? e => e.stopPropagation() : null}
      {...option}
    >
      {content}
    </RightContainer>
  );
}
